import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type { OnboardingInput, OnboardingItemInput } from './types';

export const onboardingKeys = {
  all: ['onboardings'] as const,
  lists: () => [...onboardingKeys.all, 'list'] as const,
  list: (p: api.OnboardingListParams) => [...onboardingKeys.lists(), p] as const,
  detail: (id: string | number) => [...onboardingKeys.all, 'detail', id] as const,
};

/** Truthy id that isn't the `-1` "skip" sentinel used by optional lookups. */
function isResolvable(id: string | number): boolean {
  return id !== '' && id !== -1 && id !== '-1' && id != null;
}

export function useOnboardings(params: api.OnboardingListParams = {}) {
  return useQuery({
    queryKey: onboardingKeys.list(params),
    queryFn: () => api.listOnboardings(params),
    placeholderData: keepPreviousData,
  });
}

export function useOnboarding(id: string | number) {
  return useQuery({
    queryKey: onboardingKeys.detail(id),
    queryFn: () => api.getOnboarding(id),
    // Resolves by public_id (string, from the URL) or integer pk; the backend
    // accepts either (ADR-041).
    enabled: isResolvable(id),
  });
}

export function useCreateOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OnboardingInput) => api.createOnboarding(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: onboardingKeys.lists() });
      toast.success('Checklist created');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string | number; input: Partial<OnboardingInput> }) =>
      api.updateOnboarding(id, input),
    onSuccess: (onboarding) => {
      qc.invalidateQueries({ queryKey: onboardingKeys.lists() });
      qc.setQueryData(onboardingKeys.detail(onboarding.id), onboarding);
      qc.setQueryData(onboardingKeys.detail(onboarding.public_id), onboarding);
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useDeleteOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteOnboarding(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: onboardingKeys.lists() });
      toast.success('Checklist deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

// ---- Items: items are nested in both the list and the detail reads, so
// refresh every onboarding query (list + detail) after a write. ----
export function useCreateOnboardingItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OnboardingItemInput) => api.createOnboardingItem(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: onboardingKeys.all }),
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useUpdateOnboardingItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<OnboardingItemInput> }) =>
      api.updateOnboardingItem(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: onboardingKeys.all }),
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useDeleteOnboardingItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteOnboardingItem(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: onboardingKeys.all }),
    onError: (err: ApiError) => toast.error(err.message),
  });
}
